import { ChartFormat, UpdateMethod } from '@metrika/types';
import { PayloadAction } from '@reduxjs/toolkit';
import * as Sentry from '@sentry/react';
import Axios from 'axios';
import { eventChannel, Task } from 'redux-saga';
import { ActionPattern, call, cancel, cancelled, fork, put, race, select, take } from 'redux-saga/effects';
import { APIRequestError, MetrikaError } from '../../../_shared/api';
import { getChart } from '../../api';
import { RemoteChart } from '../../api/io';
import type { ChartState } from '../../charts/_types';
import type { DashboardFilter } from '../../hooks/dashFilters.types';
import {
   disableCharting,
   enableCharting,
   subscribeToChart,
   unsubscribeFromChart,
   newChartDataV2,
   subscribedToChart,
} from '../reducers';
import {
   makeGetUpdateStart,
   makeGetTickInterval,
   makeGetChart,
   getChartLastTimestamp,
   makeGetFormat,
   isChartTimeseries,
} from '../selectors';

function createTimer(length: number) {
   return eventChannel((emit) => {
      const id = setInterval(() => {
         emit(new Date());
      }, length);
      return () => clearInterval(id);
   });
}

function* startPolling(id: string) {
   //todo would be better not to depend on the state, not sure a good way.
   const pollInterval: number | undefined = yield select(makeGetTickInterval(id));
   if (!pollInterval) {
      console.error(`POL >> Unable to subscribe to unloaded chart ${id}!`);
      return;
   }

   yield put(subscribedToChart({ id, updateMethod: 'polling' }));

   console.log(`POL >> Creating timer for ${id} with interval ${pollInterval}.`);
   const timer = createTimer(pollInterval);

   const { dataId }: ChartState = yield select(makeGetChart(id));

   while (true) {
      const { unsubscribe } = yield race({
         _: take(timer),
         unsubscribe: take(
            ((act: PayloadAction<string>) =>
               act.type === unsubscribeFromChart.toString() && act.payload === id) as ActionPattern<
               PayloadAction<string>
            >,
         ),
      });

      if (!unsubscribe) {
         const cancelTokenSource = Axios.CancelToken.source();
         try {
            const chartState: ChartState | undefined = yield select(makeGetChart(id));
            const chartFormat: ChartFormat = yield select(makeGetFormat(id));

            const now: Date = new Date();
            const chartLastTime: string | undefined = yield select(getChartLastTimestamp(id));
            //if chart has points, then we start from the last one, otherwise we start from the lastPollingUpdate or from begin od timerange(for stats ordatable)

            //start can be undefined for dashboard without time-range
            const start: Date | undefined =
               chartLastTime && chartState && isChartTimeseries(chartState, chartFormat)
                  ? new Date(chartLastTime)
                  : yield select(makeGetUpdateStart(id));

            console.log(`POL >> Updating ${id}`, (start || now).toISOString(), now.toISOString());

            const filters: DashboardFilter[] = yield select((s) => s.charts.filters);
            const chart: RemoteChart = yield call(getChart, {
               id: dataId,
               start: start || now,
               end: now,
               maxSamples: 1,
               filters,
               cancelToken: cancelTokenSource.token,
               interval: chartState?.interval,
               extraQuery: chartState?.metadata?.extraQuery,
            });
            console.log(`POL >> Updating ${id} with payload`, chart);
            yield put(newChartDataV2({ id, data: chart.data, interval: chart.metadata.interval }));
         } catch (e: unknown) {
            if (!(e instanceof APIRequestError)) {
               const extra = e instanceof MetrikaError ? e.extra : undefined;
               // eslint-disable-next-line import/namespace
               Sentry.captureException(e, { tags: { 'exception.type': 'chart.polling' }, extra });
            }
         } finally {
            const hasBeenCancelled: boolean = yield cancelled();
            if (hasBeenCancelled) {
               cancelTokenSource.cancel('cancelled from user');
               console.log(`API >> CANCELLED polling chart ${id}`);
            }
         }
      } else break;
   }

   console.log(`POL >> Unsubscribing from ${id}`);
   timer.close();
}

function* subscribe() {
   while (true) {
      const action: PayloadAction<{ id: string; updateMethod?: UpdateMethod }> = yield take(
         subscribeToChart.toString(),
      );
      const { id, updateMethod } = action.payload;
      const shouldPollUpdates = updateMethod !== 'none';

      if (shouldPollUpdates) {
         console.log(`POL >> Subscribing to `, action.payload);
         yield fork(startPolling, id);
      }
   }
}

export function* pollingService() {
   while (true) {
      yield take(enableCharting.toString());
      console.log('POL >> Starting charting service.');

      // Start services
      const services: Task[] = [yield fork(subscribe)];

      yield take(disableCharting.toString());
      console.log('POL >> Stopping charting service.');
      yield cancel(services);
   }
}
